import { hotlineQuery } from './queue-view.mjs'
// Credentials exist only in the login request. Only the opaque gateway token is persisted.
export function createGatewayClient({ fetcher, getToken, setToken, clearToken }) {
  async function request(path, options = {}) {
    if (!path.startsWith('/') || path.startsWith('//')) throw new Error('Gateway path must be same-origin')
    const token = getToken()
    const formData = typeof FormData !== 'undefined' && options.body instanceof FormData
    const response = await fetcher(path, {
      ...options, credentials: 'same-origin',
      headers: { ...(formData ? {} : { 'Content-Type': 'application/json' }), ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
    })
    let body
    try { body = await response.json() } catch { throw new Error(`Invalid gateway response (${response.status})`) }
    if (!response.ok) {
      if (response.status === 401) clearToken()
      const error = new Error(typeof body.detail === 'string' ? body.detail : `Request failed (${response.status})`)
      error.status = response.status
      throw error
    }
    return body
  }
  async function requestBlob(path, options = {}) {
    if (!path.startsWith('/') || path.startsWith('//')) throw new Error('Gateway path must be same-origin')
    const token = getToken()
    const response = await fetcher(path, {
      ...options, credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
    })
    if (!response.ok) {
      if (response.status === 401) clearToken()
      let detail = ''
      try { const body = await response.json(); detail = typeof body.detail === 'string' ? body.detail : '' } catch { /* Binary/error text is not a download. */ }
      const error = new Error(detail || `Request failed (${response.status})`)
      error.status = response.status
      throw error
    }
    const contentType = response.headers.get('content-type')?.split(';')[0].trim().toLowerCase()
    if (contentType !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') throw new Error('Invalid contact template response')
    return response.blob()
  }
  return {
    async login(username, password) {
      clearToken()
      const result = await request('/api/user/login', { method: 'POST', body: JSON.stringify({ username, password }) })
      if (typeof result.access_token !== 'string' || !result.access_token) throw new Error('Missing gateway token')
      setToken(result.access_token)
      return result
    },
    async logout() { try { return await request('/api/user/logout', { method: 'POST' }) } finally { clearToken() } },
    operationsOverview: () => request('/api/voice-ops/overview/connect', { method: 'POST', body: '{}' }),
    async queueRules(mode) {
      if (!['auto', 'collaboration'].includes(mode)) throw new Error('Unsupported business type')
      return request('/api-cc-agent/callcenter/agent/v1/querydistrules', { method: 'POST', body: JSON.stringify({ callType: mode }) })
    },
    resolveHotline: (number, mode) => request('/api-cc-agent/callcenter/agent/acd/hotlines/resolve' + hotlineQuery(number, mode)),
    phoneSession: () => request('/api/phone/session'),
    agentSkillContext: loginAccount => request('/api-cc-agent/callcenter/agent/v1/acd/skills/list', { method: 'POST', body: JSON.stringify({ loginAccount }) }),
    saveAgentSkills: payload => request('/api-cc-agent/callcenter/agent/v1/acd/skills/update', { method: 'POST', body: JSON.stringify(payload) }),
    async saveCatalog(action, payload) {
      if (!['create', 'update'].includes(action)) throw new Error('Unsupported catalog action')
      return request(`/api-cc-agent/callcenter/agent/v1/acd/orgskills/${action}`, { method: 'POST', body: JSON.stringify(payload) })
    },
    async managementList(kind, { page = 1, pageSize = 20 } = {}) {
      if (!['skills', 'orgskills'].includes(kind)) throw new Error('Unknown management resource')
      if (!Number.isInteger(page) || page < 1 || !Number.isInteger(pageSize) || pageSize < 1 || pageSize > 100) throw new Error('Invalid management pagination')
      return request(`/api-cc-agent/callcenter/agent/v1/acd/${kind}/list`, { method: 'POST', body: JSON.stringify({ page, pageSize }) })
    },
    async agents(company) {
      if (!company) throw new Error('Missing verified organization')
      return request('/api-cc-agent/callcenter/agent/v1/querycompanyagents', { method: 'POST', body: JSON.stringify({ orgName: company }) })
    },
    async groupAgents(company) {
      if (!company) throw new Error('Missing verified organization')
      return request('/api-cc-agent/callcenter/agent/v1/querycompanyallgroupagents', { method: 'POST', body: JSON.stringify({ orgName: company, includeDisabledAgents: true }) })
    },
    async groups(company) {
      if (!company) throw new Error('Missing verified organization')
      return request('/api-cc-agent/callcenter/agent/v1/querygroups', { method: 'POST', body: JSON.stringify({ orgName: company }) })
    },
    async pdsTasks({ orgName, limit, offset }) {
      if (!orgName) throw new Error('Missing verified organization')
      if (!Number.isInteger(limit) || limit < 1 || limit > 100) throw new Error('Invalid task page limit')
      if (!Number.isInteger(offset) || offset < 0 || offset > 10000) throw new Error('Invalid task page offset')
      return request('/api-cc-backend/callcenter/call/predict/querytasks', { method: 'POST', body: JSON.stringify({ orgName, limit, offset }) })
    },
    async pdsTaskDetails({ taskId, limit, offset }) {
      assertTaskId(taskId)
      if (!Number.isInteger(limit) || limit < 1 || limit > 100) throw new Error('Invalid task detail page limit')
      if (!Number.isInteger(offset) || offset < 0 || offset > 10000) throw new Error('Invalid task detail page offset')
      return request('/api-cc-backend/callcenter/call/predict/querytaskdetails/v2', { method: 'POST', body: JSON.stringify({ taskId, limit, offset }) })
    },
    async controlPdsTask({ taskId, action }) {
      assertTaskId(taskId)
      const suffix = PDS_CONTROL_SUFFIX[action]
      if (!suffix) throw new Error('Invalid outbound campaign action')
      return request(`/api-cc-backend/callcenter/call/predict/${suffix}`, { method: 'POST', body: JSON.stringify({ taskId, action }) })
    },
    pdsContactTemplates: () => request('/api-cc-backend/callcenter/call/predict/getcustomerinfotemplatelist', { method: 'POST', body: '{}' }),
    async downloadPdsContactTemplate(templateName) {
      if (typeof templateName !== 'string' || !templateName) throw new Error('Invalid outbound contact template')
      return requestBlob('/api-cc-backend/callcenter/call/predict/getcontacttemplate', { method: 'POST', body: JSON.stringify({ templateName }) })
    },
    async uploadPdsContacts({ file, templateName }) {
      if (!(typeof File !== 'undefined' && file instanceof File) || typeof templateName !== 'string' || !templateName) throw new Error('Invalid outbound contact upload')
      const body = new FormData()
      body.append('file', file)
      body.append('templateName', templateName)
      return request('/api-cc-backend/callcenter/call/predict/uploadcontactlist', { method: 'POST', body })
    },
    async checkPdsTask({ uploadId, groupName, agentName }) {
      assertCreateTarget(uploadId, groupName, agentName)
      return request('/api-cc-backend/callcenter/call/predict/checktask', { method: 'POST', body: JSON.stringify({ uploadId, groupName, agentName }) })
    },
    async createPdsTask({ uploadId, name, groupName, agentName, startTime }) {
      assertCreateTarget(uploadId, groupName, agentName)
      if (typeof name !== 'string' || !name.trim() || name.trim().length > 64) throw new Error('Invalid outbound campaign name')
      const payload = { uploadId, name: name.trim(), groupName, agentName }
      if (startTime) payload.startTime = startTime
      return request('/api-cc-backend/callcenter/call/predict/createtask', { method: 'POST', body: JSON.stringify(payload) })
    },
    async knowledgeBases({ page, pageSize, name = '' }) {
      if (!Number.isInteger(page) || page < 1) throw new Error('Invalid knowledge base page')
      if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 100) throw new Error('Invalid knowledge base page size')
      const query = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (name.trim()) query.set('name', name.trim())
      return request(`/api/knowledge-base?${query}`, { method: 'GET' })
    },
    async knowledgeBase(id) {
      if (!Number.isInteger(id) || id <= 0) throw new Error('Invalid knowledge base identifier')
      return request(`/api/knowledge-base/${id}`, { method: 'GET' })
    },
    async chatSessions({ page, size }) {
      if (!Number.isInteger(page) || page < 1) throw new Error('Invalid chat history page')
      if (!Number.isInteger(size) || size < 1 || size > 100) throw new Error('Invalid chat history page size')
      return request('/chatbot-api/api/history/session/list', { method: 'POST', body: JSON.stringify({ page, size }) })
    },
    async chatSessionDetail(id) {
      if (!Number.isInteger(id) || id <= 0) throw new Error('Invalid chat session identifier')
      return request('/chatbot-api/api/history/session/detail', { method: 'POST', body: JSON.stringify({ id }) })
    },
    async robotTasks(filters) {
      return request('/api/robot-business/tasks', { method: 'POST', body: JSON.stringify(filters) })
    },
    async robotTaskDetail({ taskId, limit, offset }) {
      assertOpaqueId(taskId, 'robot task')
      if (!Number.isInteger(limit) || limit < 1 || limit > 100) throw new Error('Invalid robot task detail limit')
      if (!Number.isInteger(offset) || offset < 0 || offset > 10000) throw new Error('Invalid robot task detail offset')
      return request('/api/robot-business/task-detail', { method: 'POST', body: JSON.stringify({ taskId, limit, offset }) })
    },
    async robotCalls(filters) {
      return request('/api/robot-business/calls', { method: 'POST', body: JSON.stringify(filters) })
    },
    async robotCallDetail(callId) {
      assertOpaqueId(callId, 'robot call')
      return request('/api/robot-business/call-detail', { method: 'POST', body: JSON.stringify({ callId }) })
    },
    async robotAgentRecords(callIds) {
      if (!Array.isArray(callIds) || callIds.length < 1 || callIds.length > 20 || new Set(callIds).size !== callIds.length) {
        throw new Error('Invalid agent history call ids')
      }
      callIds.forEach(callId => assertOpaqueId(callId, 'robot call'))
      return request('/api/robot-business/agent-records', { method: 'POST', body: JSON.stringify({ callIds }) })
    },
  }
}
const PDS_CONTROL_SUFFIX = Object.freeze({ start: 'starttask', pause: 'pausetask', resume: 'resumetask', end: 'endtask', delete: 'deletetask' })
function assertTaskId(taskId) {
  if (!((typeof taskId === 'string' && taskId.trim()) || (Number.isInteger(taskId) && taskId >= 0))) throw new Error('Invalid task identifier')
}
function assertOpaqueId(value, label) {
  if (typeof value !== 'string' || !/^[A-Za-z0-9._:-]{1,128}$/.test(value)) throw new Error(`Invalid ${label} identifier`)
}
function assertCreateTarget(uploadId, groupName, agentName) {
  if (typeof uploadId !== 'string' || !uploadId || typeof groupName !== 'string' || !groupName
      || !Array.isArray(agentName) || agentName.length > 1 || agentName.some(value => typeof value !== 'string' || !value)) {
    throw new Error('Invalid outbound campaign target')
  }
}
export function parseAgentRows(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Agent query failed')
  const rows = Array.isArray(body.data) ? body.data : body.body?.list ?? body.data?.list
  if (!Array.isArray(rows)) throw new Error('Unexpected agent response format')
  return rows
}
export function parsePhoneIdentity(raw) {
  if (raw.code !== undefined && raw.code !== 0) throw new Error(raw.message || 'Phone identity rejected')
  const data = raw.body ?? raw
  if (!data.agentInfo || !data.userInfo) throw new Error('Phone identity incomplete')
  const agent = data.agentInfo
  const user = data.userInfo
  return {
    loginAccount: user.loginAccount || agent.loginAccount || '',
    companyName: agent.companyName || user.companyName || '',
    groupName: agent.groupName || user.groupName || '',
    sipUser: agent.sipuser || agent.sipUser || '',
    callType: agent.callType ?? '',
  }
}
export function resolveAiccCompany(profile) {
  // Legacy gateways omit this field only when both services use the same tenant name.
  const company = profile && Object.prototype.hasOwnProperty.call(profile, 'aicc_company_name') ? profile.aicc_company_name : profile?.company_name
  return typeof company === 'string' ? company : ''
}
export function parsePdsTaskRows(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Outbound campaign query failed')
  const result = body.body ?? body.data
  if (!result || !Array.isArray(result.list) || typeof result.total !== 'number') throw new Error('Unexpected outbound campaign response format')
  return { total: result.total, list: result.list }
}
export function parsePdsTaskDetailRows(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Outbound campaign detail query failed')
  const result = body.body ?? body.data
  if (!result || !Array.isArray(result.list) || typeof result.total !== 'number') throw new Error('Unexpected outbound campaign detail response format')
  return { total: result.total, list: result.list }
}
export function parsePdsControlResult(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Outbound campaign operation failed')
  return body.body ?? body.data ?? {}
}
export function parsePdsTemplates(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Outbound template query failed')
  const result = body.body ?? body.data
  if (!result || !Array.isArray(result.list)) throw new Error('Unexpected outbound template response format')
  return result.list
}
export function parsePdsUpload(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Outbound contact upload failed')
  const result = body.body ?? body.data
  if (!result || typeof result.uploadId !== 'string' || !result.uploadId) throw new Error('Unexpected outbound contact upload response')
  return result.uploadId
}
export function parsePdsCreateStep(body, fallback) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || fallback)
  return body.body ?? body.data ?? {}
}
export function parseKnowledgeBaseRows(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Knowledge base query failed')
  const data = body.data ?? body.body
  if (!data || !Array.isArray(data.items) || typeof data.total !== 'number' || typeof data.max_page !== 'number') throw new Error('Unexpected knowledge base response format')
  return { items: data.items, total: data.total, maxPage: data.max_page }
}
export function parseKnowledgeBaseDetail(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Knowledge base detail failed')
  const data = body.data ?? body.body
  if (!data || !Number.isInteger(data.id) || !Array.isArray(data.versions)) throw new Error('Unexpected knowledge base detail format')
  return data
}
export function parseChatSessionRows(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Chat history query failed')
  const data = body.data ?? body.body
  if (!data || !Array.isArray(data.list) || typeof data.total !== 'number') throw new Error('Unexpected chat history response format')
  return { list: data.list, total: data.total }
}
export function parseChatSessionDetail(body) {
  if (body.code !== 0 && body.code !== 200 && body.code !== '0') throw new Error(body.message || body.msg || 'Chat history detail failed')
  const data = body.data ?? body.body
  if (!data?.session || !Array.isArray(data.messages)) throw new Error('Unexpected chat history detail format')
  return { session: data.session, messages: data.messages, agent_history: Array.isArray(data.agent_history) ? data.agent_history : [] }
}
export function parseRobotTaskRows(body) {
  const data = body?.data
  if (body?.code !== 0 || !data || !Array.isArray(data.list) || typeof data.has_more !== 'boolean') throw new Error('Unexpected robot task response format')
  return { list: data.list, hasMore: data.has_more }
}
export function parseRobotTaskDetailRows(body) {
  const data = body?.data
  if (body?.code !== 0 || !data || !Array.isArray(data.list) || typeof data.total !== 'number') throw new Error('Unexpected robot task detail format')
  return { list: data.list, total: data.total }
}
export function parseRobotCallRows(body) {
  const data = body?.data
  if (body?.code !== 0 || !data || !Array.isArray(data.list) || typeof data.total !== 'number') throw new Error('Unexpected robot call response format')
  return { list: data.list, total: data.total }
}
export function parseRobotCallDetail(body) {
  const data = body?.data
  if (body?.code !== 0 || !data || typeof data.callId !== 'string' || typeof data.phone !== 'string'
      || typeof data.talkTime !== 'number' || typeof data.talkRounds !== 'number'
      || typeof data.callStartTime !== 'string' || typeof data.intention !== 'string'
      || typeof data.robotVersion !== 'string' || !Array.isArray(data.dialogue)
      || data.dialogue.some(turn => !turn || !['robot', 'customer'].includes(turn.speaker)
        || typeof turn.message !== 'string' || typeof turn.startTime !== 'string')) {
    throw new Error('Unexpected robot call detail format')
  }
  return { ...data, dialogue: data.dialogue.filter(turn => turn.message.trim().length > 0) }
}
